import { Suspense, type ReactNode } from 'react'
import { I18nextProvider } from 'react-i18next'
import { QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import i18n from './i18n'
import { queryClient } from '@/lib/react-query'
import { AuthProvider, useAuth } from '@/components/AuthProvider'
import { PostHogProvider } from '@/components/PostHogProvider'
import { BackgroundManager } from '@/components/BackgroundManager'
import { InviteRedirect } from '@/components/InviteRedirect'
import { PowerSyncProvider } from '@/components/PowerSyncProvider'
import { ClientBoot } from '@/components/ClientBoot'
import { DebugNukeButton, PostNukeCleanup } from '@/components/DebugNukeButton'
import { DebugDiagnostics } from '@/components/DebugDiagnostics'
import { AppBootGate } from '@/components/AppBootGate'
import { AccountGate } from '@/components/AccountGate'
import { DemoStatusBanner } from '@/components/DemoStatusBanner'
import { DemoIntroDialog } from '@/components/DemoIntroDialog'
import { GlobalErrorCatcher } from '@/components/GlobalErrorCatcher'
import { VersionGate } from '@/components/VersionGate'
import { Navbar } from '@/components/Navbar'
import { MobileTabBar } from '@/components/MobileTabBar'
import { usePathname } from '@/hooks/usePathname'
import { isMarketingPath } from '@/lib/route-gates'
import { ScaffoldingDefectsMigration } from '@/components/ScaffoldingDefectsMigration'

function TopNavbar() {
  const { user, userRole } = useAuth()
  const pathname = usePathname()
  // Marketing/auth surfaces (login, setup-password, legal, …) skip the authed
  // chrome — same gate AppBootGate uses. Without this a cached or freshly
  // signed-in `user` renders the navbar over the login form.
  if (!user || isMarketingPath(pathname)) return null
  return <Navbar user={user} userRole={userRole} />
}

// MobileTabBar is a fixed bottom-of-viewport nav for narrow screens; hide it
// on lg+ where the desktop Navbar (with the "Mehr" dropdown) provides the
// same destinations.
function BottomTabBar() {
  const { user, userRole } = useAuth()
  const pathname = usePathname()
  if (!user || isMarketingPath(pathname)) return null
  return (
    <div className="lg:hidden">
      <MobileTabBar user={user} userRole={userRole ?? undefined} />
    </div>
  )
}

// Mirror of the providers tree from src/app/layout.tsx and
// src/components/ClientLayoutRouter.tsx, restructured for a SPA root. Order
// matches the original:
//   I18n → PostHog → (Background / InviteRedirect siblings) → QueryClient →
//   Auth → (PostNuke / PowerSync / AppBootGate / SyncStatus / Debug overlays)
//   → router children.
export function AppRoot({ children }: { children: ReactNode }) {
  return (
    <I18nextProvider i18n={i18n}>
      <PostHogProvider>
        <BackgroundManager />
        <InviteRedirect />
        {/* Force-update gate: overlays everything (incl. login) when the native
            binary is too old or a newer web build is deployed. Mounted outside
            AppBootGate so a too-old client is blocked before it ever renders. */}
        <VersionGate />
        <div className="relative z-10">
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <PostNukeCleanup />
              <PowerSyncProvider>
                <ClientBoot />
                <ScaffoldingDefectsMigration />
                <Suspense fallback={null}>
                  <AppBootGate>
                    <AccountGate>
                      <TopNavbar />
                      {/* Inside AccountGate so it can never cover the
                          pending/rejected screens, which replace the app. */}
                      <DemoIntroDialog />
                      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 lg:pt-8 pb-20 lg:pb-4">
                        <DemoStatusBanner />
                        {children}
                      </main>
                      <BottomTabBar />
                    </AccountGate>
                  </AppBootGate>
                </Suspense>
                <DebugNukeButton />
                <DebugDiagnostics />
              </PowerSyncProvider>
              <Toaster position="top-center" toastOptions={{ style: { zIndex: 99999 } }} />
              <GlobalErrorCatcher />
            </AuthProvider>
          </QueryClientProvider>
        </div>
      </PostHogProvider>
    </I18nextProvider>
  )
}
